import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ModalProps, ModalsService } from '../../../../services/modals.service';
import { AlertService } from '../../../../services/alert.service';
import { useModalClose } from '../../../../utils/useModalClose';
import { InputWrapper } from '../../../common/input-wrapper';
import { Checkbox } from '../../../common/checkbox';
import { User, UserMultiSelect } from '../../../common/user-multi-select';
import useSWRMutation from 'swr/mutation';
import { trpc, RouterInput, RouterOutput } from '../../../../api/trpc';
import {
  FEATURE_FLAGS,
  FEATURE_FLAG_LABELS,
} from '../../../../consts/feature-flags';
import { ConfirmModal } from '../../../common/confirm-modal';

type FlagData = RouterOutput['featureFlag']['list'][number];

interface Props extends ModalProps {
  flag: FlagData;
  mutate: () => void;
  readOnly?: boolean;
}

type FormValues = {
  enabledForAll: boolean;
  users: User[];
  restartUsers: User[];
};

export const FeatureFlagEditModal = ({
  flag,
  mutate,
  close,
  readOnly = false,
}: Props) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const {
    control,
    handleSubmit,
    watch,
    formState: { dirtyFields },
  } = useForm<FormValues>({
    defaultValues: {
      enabledForAll: flag.enabledForAll,
      users: flag.users,
      restartUsers: [],
    },
  });

  const enabledForAll = watch('enabledForAll');
  const restartUsers = watch('restartUsers');
  const hasUnsavedAccessChanges = !!(
    dirtyFields.enabledForAll || dirtyFields.users
  );
  const isOnboarding = flag.name === FEATURE_FLAGS.ONBOARDING;
  const isGlobalOnly =
    flag.name === FEATURE_FLAGS.PUBLIC_CATALOG ||
    flag.name === FEATURE_FLAGS.MAINPAGE_1688_NEW_CLIENTS;

  const { trigger: update, isMutating } = useSWRMutation(
    ['featureFlag.update'],
    (_k, { arg }: { arg: RouterInput['featureFlag']['update'] }) =>
      trpc.featureFlag.update.mutate(arg),
  );

  const { trigger: restart, isMutating: isRestarting } = useSWRMutation(
    ['featureFlag.restartOnboarding'],
    (_k, { arg }: { arg: RouterInput['featureFlag']['restartOnboarding'] }) =>
      trpc.featureFlag.restartOnboarding.mutate(arg),
  );
  const isBusy = isMutating || isRestarting || isConfirming;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await update({
        id: flag.id,
        enabledForAll: values.enabledForAll,
        // Для онбординга сохраняем индивидуальный список и при общем доступе,
        // чтобы назначения остались после выключения «Для всех клиентов».
        userUuids:
          isGlobalOnly || (values.enabledForAll && !isOnboarding)
            ? []
            : values.users.map((user) => user.id),
      });
      AlertService.success();
      mutate();
      close();
    } catch {
      AlertService.error('Не удалось сохранить настройки фича-флага');
    }
  });

  const onRestart = async () => {
    if (hasUnsavedAccessChanges || restartUsers.length === 0) return;
    const ids = restartUsers
      .map((user) => user.internalId ?? user.id)
      .join(', ');
    setIsConfirming(true);
    try {
      await ModalsService.createModal(ConfirmModal, {
        title: 'Открыть обучение выбранным клиентам?',
        description: `Клиенты ID ${ids} получат доступ и начнут обучение с первого шага.`,
        buttonText: 'Открыть обучение',
      });
    } catch {
      return;
    } finally {
      setIsConfirming(false);
    }

    try {
      await restart({
        id: flag.id,
        restartUserUuids: restartUsers.map((user) => user.id),
      });
      AlertService.success(
        'Доступ к обучению открыт. Выбранные клиенты увидят его после обновления страницы.',
      );
      mutate();
      close();
    } catch {
      AlertService.error('Не удалось открыть обучение');
    }
  };

  const modalRef = useModalClose(() => {
    if (!isBusy) close();
  });

  const renderUserSelect = (name: 'users' | 'restartUsers') => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div
          role="presentation"
          onMouseDown={(event) => event.stopPropagation()}
          onTouchStart={(event) => event.stopPropagation()}
        >
          {/* При очистке react-select удаляет крестик до проверки
          useModalClose на document. Не даём считать клик внешним. */}
          <UserMultiSelect
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={
              isOnboarding
                ? name === 'users'
                  ? 'Нет клиентов с доступом'
                  : 'Найдите клиента по ID, имени или телефону'
                : 'Выберите пользователей'
            }
            roles={isOnboarding ? ['client'] : ['admin', 'client', 'employee']}
            isDisabled={readOnly || isBusy}
            allowAdd={!isOnboarding || name !== 'users'}
          />
        </div>
      )}
    />
  );

  return (
    <div className="modal-dialog" ref={modalRef}>
      <div className="modal-content">
        <div className="modal-header border-bottom-0 p-4 d-flex gap-2">
          <div className="text-left mx-auto mt-1">
            <h5>Фича флаг: {FEATURE_FLAG_LABELS[flag.name] ?? flag.name}</h5>
          </div>
          <button
            type="button"
            className="btn-close"
            disabled={isBusy}
            onClick={() => close()}
          />
        </div>
        <div className="modal-body pt-2 p-4">
          <form
            onSubmit={readOnly ? (event) => event.preventDefault() : onSubmit}
          >
            <InputWrapper
              className="mb-3"
              title={
                isOnboarding ? <h6>Доступ к онбордингу</h6> : 'Включён для всех'
              }
            >
              <Controller
                control={control}
                name="enabledForAll"
                render={({ field }) => (
                  <Checkbox
                    title={
                      isOnboarding
                        ? 'Для всех клиентов'
                        : 'Для всех пользователей'
                    }
                    checked={field.value}
                    disabled={readOnly || isBusy}
                    onChange={({ target: { checked } }) =>
                      field.onChange(checked)
                    }
                  />
                )}
              />
            </InputWrapper>

            {isOnboarding && (
              <p className="text-muted">
                {enabledForAll
                  ? 'Онбординг доступен всем клиентам.'
                  : 'Онбординг доступен только клиентам из списка «Клиенты с доступом». После завершения всех разделов или выбора «Больше не показывать» клиент исчезает из списка.'}
              </p>
            )}

            {!isGlobalOnly && !enabledForAll && (
              <InputWrapper
                className="mb-3"
                title={isOnboarding ? 'Клиенты с доступом' : 'Пользователи'}
              >
                {renderUserSelect('users')}
                {isOnboarding && !readOnly && (
                  <div className="form-text">
                    Удаление применяется после нажатия «Сохранить».
                  </div>
                )}
              </InputWrapper>
            )}

            {!readOnly && (
              <button
                type="submit"
                className="btn btn-success w-100 mt-3"
                disabled={isBusy}
              >
                Сохранить
              </button>
            )}
            {isOnboarding && (
              <div className="border-top mt-4 pt-4">
                <h6>Открыть обучение</h6>
                <p className="text-muted">
                  Выбранные клиенты получат доступ и начнут обучение с первого
                  шага.
                </p>
                <InputWrapper className="mb-3" title="Выберите клиентов">
                  {renderUserSelect('restartUsers')}
                </InputWrapper>
                {!readOnly && (
                  <>
                    {hasUnsavedAccessChanges && (
                      <p className="text-muted mb-2" role="status">
                        Сначала сохраните изменения доступа.
                      </p>
                    )}
                    <button
                      type="button"
                      className="btn btn-outline-primary w-100"
                      disabled={
                        isBusy ||
                        hasUnsavedAccessChanges ||
                        restartUsers.length === 0
                      }
                      onClick={onRestart}
                    >
                      Открыть обучение
                    </button>
                  </>
                )}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
